'use client'

import { IChildrenProps } from '@/utils/typings/children-props'
import { createContext, useContext, useState } from 'react'
import { useDirectorySelectionManager } from '@/features/drive/functionalities/selection/use-directory-context-selection'
import { INode } from '@/api/nodes/mutations/directory/create-directory-mutation'
import { useDirectoryContext } from '@/features/drive/entities/directory/directory-context'
import { ConfirmationModal } from '@/components/ui/confirmation-modal'

type IDirectorySelectionContext = ReturnType<typeof useDirectorySelectionManager> & {
    onSelect?: (nodes: INode[]) => void
    setMaxSelectCount: (count: number | undefined) => void
}

const DirectorySelectionContext = createContext<IDirectorySelectionContext>({} as IDirectorySelectionContext)

export type IDirectorySelectionContextProviderProps = IChildrenProps &
    Pick<IDirectorySelectionContext, 'onSelect'> & {
        maxSelectCount?: number
    }

export const DirectorySelectionContextProvider = ({
    children,
    onSelect,
    maxSelectCount: initialMaxSelectCount,
}: IDirectorySelectionContextProviderProps) => {
    const { directory, content, nodePath } = useDirectoryContext()

    const [maxSelectCount, setMaxSelectCount] = useState<number | undefined>(initialMaxSelectCount)

    const selectionManager = useDirectorySelectionManager({
        directory,
        content: content.allContent,
        nodePath,
        nodeContentQueryKey: content.nodeContentQueryKey,
        maxSelectCount,
    })

    return (
        <DirectorySelectionContext.Provider
            value={{
                onSelect,
                setMaxSelectCount,
                ...selectionManager,
            }}
        >
            <ConfirmationModal
                confirmText="Delete"
                title="Are you sure?"
                subtitle="This action cannot be undone."
                content="You are about to delete the selected items."
                destructive
                open={selectionManager.deletion.isDeleteSelectedOpen}
                onOpenChange={(open) => {
                    if (!open) selectionManager.deletion.closeDeleteSelectedDialog()
                }}
                onConfirm={selectionManager.deletion.deleteSelected}
            />

            {children}
        </DirectorySelectionContext.Provider>
    )
}

export const useDirectorySelectionContext = () => useContext(DirectorySelectionContext)
